import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
    MessageCircle,
    X,
    Send,
    Paperclip,
    Image as ImageIcon,
    ChevronLeft,
    Circle,
    Check,
    CheckCheck,
    Mic
} from 'lucide-react';
import { toast } from 'sonner';
import { VoiceRecorder, VoiceMessagePlayer, TextToSpeechButton } from './VoiceComponents';
import { linkifyText } from '../utils/linkify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role labels in French
const ROLE_LABELS = {
    admin: 'Admin',
    super_admin: 'Super Admin',
    vendeur: 'Vendeur',
    confirmateur: 'Confirmateur',
    driver: 'Livreur',
    office_agent: 'Agent'
};

const ChatWidget = () => {
    const { user } = useAuth();
    const [token, setToken] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    
    const wsRef = useRef(null);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const fileInputRef = useRef(null);
    
    // Get token from localStorage
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        setToken(storedToken);
    }, [user]);
    
    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    
    // Fetch contacts
    const fetchContacts = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/chat/contacts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setContacts(data.contacts || []);
                setOnlineUsers(data.online_users || []);
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
        }
    }, [token]);
    
    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/chat/unread-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUnreadCount(data.unread_count || 0);
            }
        } catch (err) {
            console.error('Error fetching unread count:', err);
        }
    }, [token]);
    
    // Fetch messages for selected contact
    const fetchMessages = useCallback(async (contactId) => {
        if (!token || !contactId) return;
        try {
            const res = await fetch(`${API_URL}/api/chat/messages/${contactId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data.messages || []);
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        }
    }, [token]);
    
    // Connect WebSocket
    useEffect(() => {
        if (!token || !isOpen) return;
        
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = API_URL.replace('https://', '').replace('http://', '');
        const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${token}`;
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
            console.log('Chat WebSocket connected');
        };
        
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            
            if (data.type === 'connection_established') {
                setOnlineUsers(data.online_users || []);
            }
            else if (data.type === 'new_message') {
                const msg = data.message;
                // If it's from the currently selected contact, add to messages
                if (selectedContact && msg.sender_id === selectedContact.id) {
                    setMessages(prev => [...prev, msg]);
                    // Mark as read
                    ws.send(JSON.stringify({
                        type: 'read',
                        conversation_id: msg.conversation_id,
                        other_user_id: msg.sender_id
                    }));
                } else {
                    // Show notification and update unread count
                    setUnreadCount(prev => prev + 1);
                    // Update contact's unread count
                    setContacts(prev => prev.map(c => 
                        c.id === msg.sender_id 
                            ? { ...c, unread_count: (c.unread_count || 0) + 1, last_message: msg.content }
                            : c
                    ));
                }
            }
            else if (data.type === 'typing') {
                if (selectedContact && data.sender_id === selectedContact.id) {
                    setTypingUser(data.sender_name);
                    setIsTyping(true);
                    // Clear typing after 3 seconds
                    setTimeout(() => setIsTyping(false), 3000);
                }
            }
            else if (data.type === 'messages_read') {
                // Update message read status
                setMessages(prev => prev.map(m => 
                    m.conversation_id === data.conversation_id && m.sender_id === user?.id
                        ? { ...m, is_read: true }
                        : m
                ));
            }
            else if (data.type === 'pong') {
                // Keep-alive response
            }
        };
        
        ws.onclose = () => {
            console.log('Chat WebSocket disconnected');
        };
        
        ws.onerror = (error) => {
            console.error('Chat WebSocket error:', error);
        };
        
        // Keep-alive ping every 30 seconds
        const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000);
        
        return () => {
            clearInterval(pingInterval);
            ws.close();
        };
    }, [token, isOpen, selectedContact, user?.id]);
    
    // Fetch data when widget opens
    useEffect(() => {
        if (isOpen) {
            fetchContacts();
            fetchUnreadCount();
        }
    }, [isOpen, fetchContacts, fetchUnreadCount]);
    
    // Fetch messages when contact is selected
    useEffect(() => {
        if (selectedContact) {
            fetchMessages(selectedContact.id);
        }
    }, [selectedContact, fetchMessages]);
    
    // Send typing indicator
    const handleTyping = () => {
        if (!wsRef.current || !selectedContact) return;
        
        // Clear previous timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        
        // Send typing indicator
        if (wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'typing',
                recipient_id: selectedContact.id
            }));
        }
        
        // Set timeout to stop typing indicator
        typingTimeoutRef.current = setTimeout(() => {
            // Typing stopped
        }, 2000);
    };
    
    // Send message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedContact) return;
        
        try {
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: newMessage.trim()
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                setNewMessage('');
            } else {
                toast.error('Erreur lors de l\'envoi du message');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            toast.error('Erreur de connexion');
        }
    };
    
    // Handle file upload
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedContact) return;
        
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 10 Mo)');
            return;
        }
        
        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const uploadRes = await fetch(`${API_URL}/api/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (!uploadRes.ok) {
                throw new Error('Upload failed');
            }
            
            const uploadData = await uploadRes.json();
            
            // Send message with attachment
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: '',
                    attachment_type: uploadData.attachment_type,
                    attachment_url: uploadData.attachment_url,
                    attachment_name: uploadData.attachment_name
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                toast.success('Fichier envoyé');
            }
        } catch (err) {
            console.error('Error uploading file:', err);
            toast.error('Erreur lors de l\'envoi du fichier');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    
    // Handle voice recording complete
    const handleVoiceRecordingComplete = async (audioBlob, duration) => {
        if (!selectedContact || !audioBlob) return;
        
        setIsUploading(true);
        
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
            
            const uploadRes = await fetch(`${API_URL}/api/chat/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (!uploadRes.ok) {
                throw new Error('Upload failed');
            }
            
            const uploadData = await uploadRes.json();
            
            // Send message with voice attachment
            const res = await fetch(`${API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    recipient_id: selectedContact.id,
                    content: '',
                    attachment_type: 'voice',
                    attachment_url: uploadData.attachment_url,
                    attachment_name: `Message vocal (${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')})`
                })
            });
            
            if (res.ok) {
                const data = await res.json();
                setMessages(prev => [...prev, data.message]);
                toast.success('Message vocal envoyé');
            }
        } catch (err) {
            console.error('Error sending voice message:', err);
            toast.error('Erreur lors de l\'envoi du message vocal');
        } finally {
            setIsUploading(false);
        }
    };
    
    // Select a contact
    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        // Reset unread count for this contact
        setContacts(prev => prev.map(c => 
            c.id === contact.id ? { ...c, unread_count: 0 } : c
        ));
    };
    
    // Format time
    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'À l\'instant';
        if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
        if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    };
    
    // Render message content
    const renderMessageContent = (msg, isOwn) => {
        // Voice message
        if (msg.attachment_type === 'voice') {
            return (
                <VoiceMessagePlayer 
                    src={msg.attachment_url} 
                    isOwn={isOwn}
                />
            );
        }
        
        // Image
        if (msg.attachment_type === 'image') {
            return (
                <div>
                    <img 
                        src={msg.attachment_url} 
                        alt={msg.attachment_name}
                        className="max-w-[200px] rounded-lg cursor-pointer hover:opacity-90"
                        onClick={() => window.open(msg.attachment_url, '_blank')}
                    />
                    {msg.content && <p className="mt-1 text-sm">{msg.content}</p>}
                </div>
            );
        }
        
        // File
        if (msg.attachment_type === 'file') {
            return (
                <a 
                    href={msg.attachment_url}
                    download={msg.attachment_name}
                    className="flex items-center gap-2 p-2 bg-background/50 rounded-lg hover:bg-background/80"
                >
                    <Paperclip className="w-4 h-4" />
                    <span className="text-sm truncate max-w-[150px]">{msg.attachment_name}</span>
                </a>
            );
        }
        
        // Text message with TTS button
        return (
            <div className="flex items-start gap-1">
                <p className="text-sm whitespace-pre-wrap flex-1">
                    {linkifyText(msg.content, isOwn 
                        ? "text-blue-200 underline hover:text-blue-100 break-all" 
                        : "text-blue-600 underline hover:text-blue-500 break-all"
                    )}
                </p>
                {msg.content && <TextToSpeechButton text={msg.content} />}
            </div>
        );
    };
    
    // Don't render if user is not logged in
    if (!user || !token) return null;
    
    return (
        <>
            {/* Floating button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-20 right-6 z-[9999] w-14 h-14 bg-blue-600 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center hover:scale-110 hover:bg-blue-700 border-2 border-white"
                style={{ boxShadow: '0 4px 20px rgba(37, 99, 235, 0.5)' }}
                data-testid="chat-widget-toggle"
            >
                {isOpen ? (
                    <X className="w-6 h-6" />
                ) : (
                    <>
                        <MessageCircle className="w-7 h-7" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </>
                )}
            </button>
            
            {/* Chat panel */}
            {isOpen && (
                <div 
                    className="fixed bottom-36 right-6 z-[9999] w-[350px] h-[500px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                    data-testid="chat-widget-panel"
                >
                    {/* Header */}
                    <div className="p-4 border-b border-border bg-primary/5">
                        {selectedContact ? (
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => setSelectedContact(null)}
                                    className="p-1 hover:bg-background/80 rounded"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold">{selectedContact.name}</span>
                                        {onlineUsers.includes(selectedContact.id) && (
                                            <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                                        )}
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {ROLE_LABELS[selectedContact.role] || selectedContact.role}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <h3 className="font-semibold">Messages</h3>
                                <p className="text-xs text-muted-foreground">
                                    {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        )}
                    </div>
                    
                    {/* Content */}
                    {selectedContact ? (
                        <>
                            {/* Messages */}
                            <ScrollArea className="flex-1 p-3">
                                <div className="space-y-3">
                                    {messages.map((msg) => {
                                        const isOwn = msg.sender_id === user.id;
                                        return (
                                            <div
                                                key={msg.id}
                                                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`max-w-[80%] p-3 rounded-2xl ${
                                                        isOwn
                                                            ? 'bg-primary text-primary-foreground rounded-br-sm'
                                                            : 'bg-muted rounded-bl-sm'
                                                    }`}
                                                >
                                                    {renderMessageContent(msg, isOwn)}
                                                    <div className={`flex items-center gap-1 mt-1 ${
                                                        isOwn ? 'justify-end' : 'justify-start'
                                                    }`}>
                                                        <span className="text-[10px] opacity-70">
                                                            {formatTime(msg.created_at)}
                                                        </span>
                                                        {isOwn && (
                                                            msg.is_read 
                                                                ? <CheckCheck className="w-3 h-3 opacity-70" />
                                                                : <Check className="w-3 h-3 opacity-70" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {isTyping && (
                                        <div className="flex justify-start">
                                            <div className="bg-muted p-3 rounded-2xl rounded-bl-sm">
                                                <div className="flex gap-1">
                                                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                    <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>
                            </ScrollArea>
                            
                            {/* Input */}
                            <div className="p-3 border-t border-border">
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                    />
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="shrink-0"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                    >
                                        <Paperclip className="w-5 h-5" />
                                    </Button>
                                    <VoiceRecorder 
                                        onRecordingComplete={handleVoiceRecordingComplete}
                                        maxDuration={300}
                                        disabled={isUploading}
                                    />
                                    <Input
                                        value={newMessage}
                                        onChange={(e) => {
                                            setNewMessage(e.target.value);
                                            handleTyping();
                                        }}
                                        placeholder="Votre message..."
                                        className="flex-1"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                    />
                                    <Button
                                        size="icon"
                                        className="shrink-0"
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim()}
                                    >
                                        <Send className="w-5 h-5" />
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Contact list */
                        <ScrollArea className="flex-1">
                            <div className="divide-y divide-border">
                                {contacts.length === 0 ? (
                                    <div className="p-6 text-center text-muted-foreground">
                                        <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>Aucun contact disponible</p>
                                    </div>
                                ) : (
                                    contacts.map((contact) => (
                                        <button
                                            key={contact.id}
                                            onClick={() => handleSelectContact(contact)}
                                            className="w-full p-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                                            data-testid={`chat-contact-${contact.id}`}
                                        >
                                            <div className="relative">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <span className="text-primary font-medium">
                                                        {contact.name?.charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                {onlineUsers.includes(contact.id) && (
                                                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-card" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium truncate">{contact.name}</span>
                                                    {contact.last_message_at && (
                                                        <span className="text-xs text-muted-foreground">
                                                            {formatTime(contact.last_message_at)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {contact.last_message || ROLE_LABELS[contact.role] || contact.role}
                                                    </span>
                                                    {contact.unread_count > 0 && (
                                                        <Badge variant="destructive" className="text-xs h-5 min-w-5 justify-center">
                                                            {contact.unread_count}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                    )}
                </div>
            )}
        </>
    );
};

export default ChatWidget;
